/**
 * Cleans text by removing extra spaces and normalizing punctuation
 * @param {string} text - The text to clean
 * @returns {string} - The cleaned text
 */
export const cleanText = (text) => {
  if (!text) return '';
  return text
    .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
    .replace(/\s+\./g, '.') // Remove spaces before periods
    .replace(/\.\s+/g, '. ') // Ensure single space after periods
    .replace(/\s+,/g, ',') // Remove spaces before commas
    .replace(/,\s+/g, ', ') // Ensure single space after commas
    .trim(); // Remove leading/trailing spaces
};

/**
 * Prevents orphaned words in text by replacing the last space with a non-breaking space
 * @param {string} text - The text to process
 * @returns {string} - The text with the last word protected from being orphaned
 */
export const preventOrphan = (text) => {
  if (!text) return '';
  
  const words = text.split(' ');
  if (words.length <= 1) return text;
  
  const lastWord = words.pop();
  return [...words, '\u00A0' + lastWord].join(' ');
};

/**
 * Ensures a product title uses exactly two lines, keeping all words
 * @param {string} title - The product title to format
 * @returns {string} - The formatted title with all words split into two lines
 */
export const ensureTwoLines = (title) => {
  if (!title) return '';
  
  const words = title.split(' ').filter(word => word.trim());
  
  // If only one word, add empty second line
  if (words.length === 1) {
    return words[0] + '\n';
  }
  
  // For two or more words, split into two lines
  const midPoint = Math.ceil(words.length / 2);
  const firstLine = words.slice(0, midPoint).join(' ');
  const secondLine = words.slice(midPoint).join(' ');
  
  return firstLine + '\n' + secondLine;
};

/**
 * Combines both functions to ensure a product title is properly formatted
 * @param {string} title - The product title to format
 * @returns {string} - The formatted title with two lines and no orphans
 */
export const formatProductTitle = (title) => {
  const twoLineTitle = ensureTwoLines(title);
  return twoLineTitle.split('\n').map(line => preventOrphan(line)).join('\n');
};

export function splitMobileTitle(title) {
  if (!title) return ['', ''];
  
  const words = title.trim().split(/\s+/);

  // 1) If there's an ampersand, group prev + & + next on line1
  const ampIndex = words.indexOf('&');
  if (ampIndex > 0 && ampIndex < words.length - 1) {
    const line1 = words.slice(ampIndex - 1, ampIndex + 2).join(' ');
    const line2 = words.slice(ampIndex + 2).join(' ');
    return [line1, line2];
  }

  // 2) Otherwise, for any 2+ words: first word / the rest
  if (words.length >= 2) {
    return [
      words[0],
      words.slice(1).join(' ')
    ];
  }

  // 3) Single word stays alone
  return [words[0], ''];
}

/**
 * Splits a title for desktop cards, prioritizing & breaks and last word breaks
 * @param {string} title - The title to split
 * @returns {[string, string]} - Array of two strings for the two lines
 */
export const splitDesktopTitle = (title) => {
  if (!title) return ["", ""];
  const words = title.trim().split(/\s+/);
  
  // 1) If there's an &, group "prev & next" on line 1
  const amp = words.indexOf("&");
  if (amp > 0 && amp < words.length - 1) {
    const line1 = words.slice(amp - 1, amp + 2).join(" ");
    const line2 = words.slice(amp + 2).join(" ");
    return [line1, line2];
  }
  
  // 2) Otherwise split before last word
  if (words.length >= 2) {
    return [
      words.slice(0, -1).join(" "),
      words[words.length - 1]
    ];
  }
  
  // 3) Single word
  return [words[0], ""];
};

export const formatSlogan = (slogan) => {
  if (!slogan) return '';
  
  // Clean the text first
  const cleanedText = cleanText(slogan);
  const words = cleanedText.split(' ');
  
  // If we have 2 or fewer words, return as is
  if (words.length <= 2) return cleanedText;
  
  // For 3 or more words, try to split into two balanced lines
  const midPoint = Math.ceil(words.length / 2);
  const firstLine = words.slice(0, midPoint).join(' ');
  const secondLine = words.slice(midPoint).join(' ');
  
  return `${firstLine}\n${secondLine}`;
}; 